import { useEffect, useMemo, useState, type ReactNode } from 'react';
import type { SxProps, Theme } from '@mui/material/styles';

import { type JobTitle, useUserJobTitleAutocompleteChoices } from '@src/features/users';
import AsyncAutocompleteInput from './AsyncAutocompleteInput';
import type { InputProps } from './TextInput';

type BackendFilterValue = string | number | boolean | null | undefined;

export type JobTitleAutocompleteInputProps = {
  value?: JobTitle | null;
  onChange: (value: JobTitle | null) => void;
  onBlur?: (event: unknown) => void;
  backendFilters?: Record<string, BackendFilterValue>;
  disabled?: boolean;
  error?: boolean;
  helperText?: ReactNode;
  InputProps?: InputProps;
  label?: ReactNode;
  pageSize?: number;
  placeholder?: string;
  required?: boolean;
  sx?: SxProps<Theme>;
};

function getJobTitleOptionLabel(jobTitle: JobTitle) {
  return jobTitle.title;
}

function normalizeBackendFilters(
  filters: Record<string, BackendFilterValue> | undefined,
) {
  const queryParams: Record<string, string> = {};
  Object.entries(filters ?? {}).forEach(([key, value]) => {
    if (value === null || value === undefined || value === '') {
      return;
    }
    queryParams[key] = String(value);
  });
  return queryParams;
}

function JobTitleAutocompleteInput({
  backendFilters,
  disabled,
  error,
  helperText,
  InputProps,
  label,
  onBlur,
  onChange,
  pageSize = 50,
  placeholder = '',
  required,
  sx,
  value = null,
}: JobTitleAutocompleteInputProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');

  const resetSearchQuery = () => {
    setSearchQuery('');
    setDebouncedSearchQuery('');
  };

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      setDebouncedSearchQuery(searchQuery.trim());
    }, 300);

    return () => window.clearTimeout(timeoutId);
  }, [searchQuery]);

  const queryParams = useMemo(
    () => ({
      ...normalizeBackendFilters(backendFilters),
      page_size: String(pageSize),
      ...(debouncedSearchQuery ? { q: debouncedSearchQuery } : {}),
    }),
    [backendFilters, debouncedSearchQuery, pageSize],
  );

  const jobTitlesQuery = useUserJobTitleAutocompleteChoices({
    enabled: !disabled,
    queryParams,
  });

  return (
    <AsyncAutocompleteInput<JobTitle>
      disabled={disabled}
      error={error}
      filterOptions={(options) => options}
      getOptionLabel={getJobTitleOptionLabel}
      helperText={helperText}
      InputProps={InputProps}
      isOptionEqualToValue={(option, selectedValue) =>
        option.id === selectedValue.id
      }
      label={label}
      loading={jobTitlesQuery.isFetching}
      onBlur={(event) => {
        resetSearchQuery();
        onBlur?.(event);
      }}
      onChange={(nextValue) => {
        resetSearchQuery();
        onChange(nextValue);
      }}
      onInputChange={(_event, nextInputValue, reason) => {
        if (reason === 'input') {
          setSearchQuery(nextInputValue);
        }
        if (reason === 'clear' || reason === 'blur') {
          resetSearchQuery();
        }
      }}
      options={jobTitlesQuery.data?.results ?? []}
      placeholder={placeholder}
      required={required}
      sx={sx}
      value={value}
    />
  );
}

JobTitleAutocompleteInput.displayName = 'JobTitleAutocompleteInput';

export default JobTitleAutocompleteInput;
